import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { InventoryService } from 'src/app/business-service/inventory/inventory.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ModalsService } from 'src/app/modules/shared/services/modals/modals.service';
import { ComponentReloadService } from 'src/app/modules/shared/services/reload/component-reload.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';

@Component({
  selector: 'app-inclusions-and-exclusions',
  templateUrl: './inclusions-and-exclusions.component.html',
  styleUrls: ['./inclusions-and-exclusions.component.scss'],
})
export class InclusionsAndExclusionsComponent implements OnInit {
  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();

  @Output() updateQuote: EventEmitter<void> = new EventEmitter();
  @Output() updateInventoryItinerary: EventEmitter<void> = new EventEmitter();


  @Input() storedFormData: any;
  @Input() editInventory: boolean = false;
  @Input() editQuote: boolean = false;

  @Input() generatingVoucher: boolean = false;
  @Input() hideFooter: boolean = false;
  @Output() makeFormDirty: EventEmitter<boolean> = new EventEmitter<boolean>();

  itemsForm!: FormGroup;

  constructor(
    private _lovDataService: LovDataService,
    private _itineraryService: ItineraryService,
    private _inventoryService: InventoryService,
    private _responseNotificationService: ResponseNotificationService,
    private _globalService: GlobalService,
    private _fb: FormBuilder
  ) { }

  ngOnInit() {
    this.itemsForm = this._fb.group({
      include: [[]],
      exclude: [[]],
      inclusionNote: [''],
      exclusionNote: ['']
    });
    if (this.storedFormData) {
      this.itemsForm.patchValue(this.storedFormData);
    } else {
      this._lovDataService.getLovItineraryData('Inclusion').subscribe(data => {
        // Adding default item prop to LOV data
        data.map((item: { addedByUser: boolean; })=>{
          item.addedByUser = false;
        }) 
        this.itemsForm.patchValue({
          include: data,
        });
      });

      this._lovDataService.getLovItineraryData('Exclusion').subscribe(data => {
        // Adding default item prop to LOV data
        data.map((item: { addedByUser: boolean; })=>{
          item.addedByUser = false;
        }) 
        this.itemsForm.patchValue({
          exclude: data,
        });
      });
    }
  }

  

  async onSubmit(): Promise<boolean> {
    try {
      // Serialize the form data
      const formData = await this.serializeFormData(this.itemsForm.value);

      if (this.leadId) {
        // Handle lead-specific submission
        await this.handleLeadSubmission(formData);
      } else {
        // Handle inventory-specific submission
        await this.handleInventorySubmission(formData);
      }

      // Reset the form dirty flag and notify the parent
      this.makeFormDirty.emit(false);
      return true;
    } catch (error) {
      console.error("Error during form submission:", error);
      // Optional: Show an error notification
      // this._responseNotificationService.showErrorMessage("Failed to submit Inclusions and Exclusions. Please try again.");
      return false;
    }
  }

  private async handleLeadSubmission(formData: any): Promise<void> {
    if (this.editQuote) {
      formData.dataSaved = true;
      this.storeFormData.emit(formData); // Emit data to parent
      this.updateQuote.emit();
    } else {
      const data = await this._itineraryService.addInclusionsAndExclusions(formData).toPromise();
      this._responseNotificationService.showMessage("Inclusions And Exclusions Added!");
      data.dataSaved = true;
      this.storeFormData.emit(data); // Emit data to parent
    }
  }

  private async handleInventorySubmission(formData: any): Promise<void> {
    if (this.editInventory) {
      formData.dataSaved = true;
      this.storeFormData.emit(formData); // Emit data to parent
      this.updateInventoryItinerary.emit();
    } else {
      const data = await this._inventoryService.addInventoryInclusionsAndExclusions(formData).toPromise();
      this._responseNotificationService.showMessage("Inclusions And Exclusions Added!");
      data.dataSaved = true;
      this.storeFormData.emit(data); // Emit data to parent
    }
  }






  async sendDataToVoucher() {
    try {
      // Await the result of serializeFormData()
      const formData = await this.serializeFormData(this.itemsForm.value);
      this.storeFormData.emit(formData);
    } catch (error) {
      console.error("Error during form serialization:", error);
    }
  }


  private async serializeFormData(formData:any): Promise<any> {
    const serializedData = {
      ...formData,
      leadId: this.leadId,
      itineraryId: this.itineraryId,
      organizationId: this.storedFormData?.organizationId,
      id: this.storedFormData ? this.storedFormData.id : null,
      createdBy: this.storedFormData ? this.storedFormData.createdBy : null,
      ...(this.storedFormData && { createDate: this.storedFormData.createDate })
    }

    return serializedData;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext(){
    if(await this.onSubmit()){
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.itemsForm.dirty || this.itemsForm.invalid) && this.itemsForm.touched;
  }

  setDefaultLov(item:any, type:string, formControlName:string){
    const payload:any = {
      name: item.name
    }
    this._lovDataService.addDefaultLovData(type, payload).subscribe({
      next: (res) => {
        this._responseNotificationService.showMessage("Item added to default list");
        // make addedByUser false for item
        const currentItems = this.itemsForm.get(formControlName)?.value || [];
        const updatedItems = currentItems.map((i: any) => {
          if(i.key === item.key){
            i.addedByUser = false;
          }
          return i;
        });
        this.itemsForm.patchValue({ [formControlName]: updatedItems });
        this.itemsForm.markAsDirty();
        this.itemsForm.markAsTouched();
      },
      error: (err) => {
        console.error("Error adding default", err);
        this._responseNotificationService.showMessage("Error adding default", true);
      }
    });
  }

  async removeDefaultLov(item:any, type:string, formControlName:string){
    let flag = await this._globalService.confirmActionPopup("Removing the item from default list will affect all users in the organization. Please confirm to proceed.", "Confirm to proceed", "Confirm");
    if(!flag){
      return;
    }
    this._lovDataService.removeDefaultLovData(type, item.key).subscribe({
      next: (res) => {
        this._responseNotificationService.showMessage("Item removed from default list");
        const currentItems = this.itemsForm.get(formControlName)?.value || [];
        const updatedItems = currentItems.map((i: any) => {
        if (i.key === item.key) {
          i.addedByUser = true;
        }
        return i;
        });
        this.itemsForm.patchValue({ [formControlName]: updatedItems });
        this.itemsForm.markAsDirty();
        this.itemsForm.markAsTouched();
      },
      error: (err) => {
        console.error("Error removing default", err);
        this._responseNotificationService.showMessage("Error removing default", true);
      }
    });
  }
}
